import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import {
  Search, Star, Archive, Trash2, Reply, Forward, MoreHorizontal,
  Clock, RefreshCw, Sparkles, Zap, MessageSquare, Tag, TrendingUp,
  ChevronDown, ChevronUp, Send, Cpu, PanelRightClose, PanelRightOpen,
  PanelLeftOpen,
  Target, CheckCircle2, AlertCircle, Calendar, UserX, HelpCircle,
  RotateCcw, Lightbulb
} from 'lucide-react'
import api from '../../lib/api.jsx'
import MessageBubble from './MessageBubble'
import MoreFilters from './MoreFilters'
import CampaignDrawer from './CampaignDrawer'
import './Inbox.css'

/* ══════════════════════════════════════════════
   MOCK DATA
   ══════════════════════════════════════════════ */

const TAG_CONFIG = {
  'interested':     { color: '#16a34a', bg: 'rgba(34,197,94,0.12)',   label: 'Interested',      icon: TrendingUp },
  'not-interested': { color: '#dc2626', bg: 'rgba(239,68,68,0.12)',   label: 'Not Interested',  icon: UserX },
  'meeting-booked': { color: '#4f46e5', bg: 'rgba(99,102,241,0.12)',  label: 'Meeting Booked',  icon: Calendar },
  'auto-reply':     { color: '#d97706', bg: 'rgba(245,158,11,0.12)',  label: 'Auto Reply',      icon: RotateCcw },
  'ooo':            { color: '#ea580c', bg: 'rgba(249,115,22,0.12)',  label: 'Out of Office',   icon: AlertCircle },
  'unsubscribe':    { color: '#b91c1c', bg: 'rgba(220,38,38,0.12)',   label: 'Unsubscribe',     icon: UserX },
  'question':       { color: '#2563eb', bg: 'rgba(59,130,246,0.12)',  label: 'Has Question',    icon: HelpCircle },
  'follow-up':      { color: '#0d9488', bg: 'rgba(20,184,166,0.12)', label: 'Follow Up',       icon: RotateCcw },
}

/* ══════════════════════════════════════════════
   API HELPERS (real call + mock fallback)
   ══════════════════════════════════════════════ */

const fetchThread = async (id) => {
  try {
    const res = await api.get(`/inbox/conversations/${id}`)
    return res.data
  } catch (err) {
    console.warn('thread fetch failed', err)
    return null
  }
}

const classifyEmail = async (conv) => {
  try {
    const res = await api.post('/ai/inbox/classify', { body: conv.messages?.at(-1)?.body })
    return res.data
  } catch {
    return { tag: conv.tag, confidence: conv.confidence, sentiment: conv.sentiment, summary: conv.aiSummary }
  }
}

const suggestReplies = async (conv) => {
  try {
    const res = await api.post('/ai/inbox/suggest-replies', {
      tag: conv.tag,
      threadBody: (conv.messages ?? []).map(m => `${m.from}: ${m.body}`).join('\n\n')
    })
    return res.data.replies
  } catch {
    return conv.smartReplies ?? []
  }
}

const draftReply = async (conv) => {
  try {
    const res = await api.post('/ai/inbox/draft-reply', {
      tag: conv.tag,
      threadBody: (conv.messages ?? []).map(m => `${m.from}: ${m.body}`).join('\n\n')
    })
    return res.data.draft
  } catch {
    return conv.smartReplies?.[0] ?? ''
  }
}

/* ══════════════════════════════════════════════
   COMPONENT HELPERS
   ══════════════════════════════════════════════ */

function AiTagBadge({ tag, size = 'small' }) {
  const cfg = TAG_CONFIG[tag]
  if (!cfg) return null
  const cls = size === 'large' ? 'rh-class-tag-large' : 'rh-ai-tag'
  return (
    <span className={`${cls} ${tag}`}>
      {cfg.label}
    </span>
  )
}

function StatusBadge({ tag }) {
  const cfg = TAG_CONFIG[tag]
  if (!cfg) return null
  return (
    <span className={`rh-status-badge ${tag}`}>
      {cfg.label}
    </span>
  )
}

function SentimentRow({ sentiment }) {
  const items = [
    { key: 'positive', emoji: '😊', label: 'Positive' },
    { key: 'neutral',  emoji: '😐', label: 'Neutral'  },
    { key: 'negative', emoji: '😞', label: 'Negative' },
  ]
  return (
    <div className="rh-sentiment-row">
      {items.map(item => (
        <div key={item.key} className={`rh-sentiment-chip ${item.key} ${sentiment === item.key ? 'active' : ''}`}>
          <span className="rh-sentiment-emoji">{item.emoji}</span>
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  )
}

/* ══════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════ */

function Inbox() {
  const [conversations, setConversations] = useState([])
  const [listLoading, setListLoading]   = useState(true)
  const [selected, setSelected]         = useState(null)
  const [threadLoading, setThreadLoading] = useState(false)
  // Single source of truth for all list filters — derived qs hits the API.
  const [filterParams, setFilterParams] = useState({
    filter: 'all',          // legacy: all | unread | starred (tag tabs map to `tags`)
    search: '',
    sort: 'time',           // time | priority
    campaignIds: [],
    tags: [],
    sentiment: [],
    accountId: null,
    dateFrom: null,         // ISO yyyy-MM-dd
    dateTo: null,
    hasReply: null,         // null | true | false
  })
  const updateFilter = useCallback((patch) => {
    setFilterParams(prev => ({ ...prev, ...patch }))
  }, [])
  const [showAiPanel, setShowAiPanel]   = useState(true)
  const [insightOpen, setInsightOpen]   = useState(true)
  const [replyMode, setReplyMode]       = useState(false)
  const [replyText, setReplyText]       = useState('')
  const [aiDrafting, setAiDrafting]     = useState(false)
  const [sendingReply, setSendingReply] = useState(false)
  const [aiLoading, setAiLoading]       = useState(false)
  const [aiData, setAiData]             = useState({})  // keyed by conv id

  // Lightweight inline toast (auto-dismiss). No toast lib in project.
  const [toast, setToast] = useState(null) // { kind: 'warning' | 'error', text: string }
  const showToast = useCallback((kind, text) => {
    setToast({ kind, text })
    setTimeout(() => setToast(null), 4000)
  }, [])

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [campaignNames, setCampaignNames] = useState({}) // { encodedId: name }
  const onDrawerNamesUpdate = useCallback((items) => {
    setCampaignNames((prev) => {
      const next = { ...prev }
      for (const it of items) next[it.campaignId] = it.campaignName
      return next
    })
  }, [])

  // Email accounts for the MoreFilters Account dropdown — lazy-loaded on
  // first expansion of the panel.
  const [accounts, setAccounts] = useState([])
  const [loadingAccounts, setLoadingAccounts] = useState(false)
  const [accountsLoaded, setAccountsLoaded] = useState(false)
  const ensureAccounts = useCallback(async () => {
    if (accountsLoaded || loadingAccounts) return
    setLoadingAccounts(true)
    try {
      const res = await api.get('/email/accounts')
      setAccounts(res.data || [])
    } catch (err) {
      console.warn('accounts fetch failed', err)
      setAccounts([])
    } finally {
      setLoadingAccounts(false)
      setAccountsLoaded(true)
    }
  }, [accountsLoaded, loadingAccounts])

  const messagesEndRef = useRef(null)

  /* ── Backend handles every filter/search/sort. List shown as-is. ── */
  const filtered = conversations

  /* ── Derived query string from filterParams ── */
  const qs = useMemo(() => {
    const p = new URLSearchParams()
    const f = filterParams
    if (f.filter && f.filter !== 'all') p.set('filter', f.filter)
    if (f.search) p.set('search', f.search)
    if (f.sort && f.sort !== 'time') p.set('sort', f.sort)
    if (f.campaignIds.length) p.set('campaign_ids', f.campaignIds.join(','))
    if (f.tags.length) p.set('tags', f.tags.join(','))
    if (f.sentiment.length) p.set('sentiment', f.sentiment.join(','))
    if (f.accountId) p.set('account_id', f.accountId)
    if (f.dateFrom) p.set('date_from', f.dateFrom)
    if (f.dateTo) p.set('date_to', f.dateTo)
    if (f.hasReply !== null) p.set('has_reply', String(f.hasReply))
    return p.toString()
  }, [filterParams])

  /* ── Load conversations list from API ── */
  const loadConversations = useCallback(async (queryString) => {
    setListLoading(true)
    try {
      const path = queryString ? `/inbox/conversations?${queryString}` : '/inbox/conversations'
      const res = await api.get(path)
      const data = res.data?.data ?? []
      setConversations(data)
      if (data.length > 0 && !selected) {
        loadThread(data[0])
      }
    } catch (err) {
      const status = err?.response?.status
      if (status === 422) {
        showToast(
          'warning',
          'Too many results for priority sort. Reverting to Latest. Narrow your filters and try again.'
        )
        updateFilter({ sort: 'time' })
      } else if (status === 400) {
        const msg = err?.response?.data?.detail?.message || 'Filter not accepted.'
        showToast('error', msg)
      } else {
        showToast('error', 'Failed to load conversations.')
        console.warn('inbox list fetch failed', err)
      }
    } finally {
      setListLoading(false)
    }
  }, [selected, updateFilter, showToast]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Load full thread for a conversation ── */
  const loadThread = useCallback(async (conv) => {
    setSelected(conv)       // show list item immediately
    setReplyMode(false)
    setReplyText('')
    setInsightOpen(true)
    setThreadLoading(true)
    try {
      const full = await fetchThread(conv.id)
      if (full) setSelected(full)
      loadAiData(full ?? conv)
    } finally {
      setThreadLoading(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Load AI data for selected conversation ── */
  const loadAiData = useCallback(async (conv) => {
    if (!conv) return
    if (aiData[conv.id]) return  // already loaded
    setAiLoading(true)
    try {
      const classification = await classifyEmail(conv)
      const taggedConv = { ...conv, tag: classification?.tag ?? conv.tag ?? '' }
      const replies = await suggestReplies(taggedConv)
      setAiData(prev => ({
        ...prev,
        [conv.id]: { classification, replies }
      }))
    } finally {
      setAiLoading(false)
    }
  }, [aiData])

  /* ── Select conversation ── */
  const handleSelect = (conv) => {
    loadThread(conv)
  }

  /* ── AI Draft ── */
  const handleAiDraft = async () => {
    setAiDrafting(true)
    setReplyMode(true)
    try {
      const classifiedTag = aiData[selected?.id]?.classification?.tag ?? selected?.tag ?? ''
      const draft = await draftReply({ ...selected, tag: classifiedTag })
      setReplyText(draft)
    } finally {
      setAiDrafting(false)
    }
  }

  /* ── AI Improve (re-draft) ── */
  const handleAiImprove = async () => {
    setAiDrafting(true)
    try {
      const classifiedTag = aiData[selected?.id]?.classification?.tag ?? selected?.tag ?? ''
      const draft = await draftReply({ ...selected, tag: classifiedTag })
      setReplyText(draft)
    } finally {
      setAiDrafting(false)
    }
  }

  /* ── Smart reply click ── */
  const handleSmartReply = (text) => {
    setReplyText(text)
    setReplyMode(true)
  }

  /* ── Send reply ── */
  const handleSendReply = async () => {
    if (!replyText.trim()) return
    setSendingReply(true)
    try {
      await api.post(`/inbox/conversations/${selected.id}/reply`, { body: replyText })
    } catch {
      // Mock: just simulate success
      await new Promise(r => setTimeout(r, 700))
    } finally {
      setSendingReply(false)
      setReplyText('')
      setReplyMode(false)
    }
  }

  /* ── Refresh conversations ── */
  const handleRefresh = () => {
    setAiData({})
    loadConversations(qs)
  }

  /* ── Debounced refetch on any qs change ── */
  useEffect(() => {
    const t = setTimeout(() => loadConversations(qs), 300)
    return () => clearTimeout(t)
  }, [qs]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Scroll to latest message when thread changes ── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [selected?.id])

  /* ── Current AI data for selected conv ── */
  const currentAi = selected ? (aiData[selected.id] || null) : null
  const currentReplies = currentAi?.replies ?? selected?.smartReplies ?? []
  const currentClassification = currentAi?.classification ?? (selected ? { tag: selected.tag, confidence: selected.confidence, sentiment: selected.sentiment } : null)

  /* ── Filter tabs ── */
  const FILTER_TABS = [
    { key: 'all',          label: 'All' },
    { key: 'unread',       label: 'Unread' },
    { key: 'starred',      label: 'Starred' },
    { key: 'interested',   label: 'Interested' },
    { key: 'question',     label: 'Question' },
    { key: 'meeting-booked', label: 'Meeting' },
    { key: 'not-interested', label: 'Not Int.' },
  ]
  const TAG_TABS = new Set(['interested', 'question', 'meeting-booked', 'not-interested'])

  const tabIsActive = (key) => {
    if (TAG_TABS.has(key)) {
      return filterParams.tags.length === 1 && filterParams.tags[0] === key
    }
    return filterParams.filter === key && filterParams.tags.length === 0
  }
  const onTabClick = (key) => {
    if (TAG_TABS.has(key)) {
      // Toggle: clicking the same tag clears it
      if (filterParams.tags.length === 1 && filterParams.tags[0] === key) {
        updateFilter({ tags: [] })
      } else {
        updateFilter({ tags: [key], filter: 'all' })
      }
    } else if (key === 'all') {
      updateFilter({ filter: 'all', tags: [] })
    } else {
      // unread / starred — legacy filter param
      updateFilter({ filter: key })
    }
  }

  return (
    <div className="replyhub-page">
      {toast && (
        <div className={`rh-toast rh-toast--${toast.kind}`} role="status">
          {toast.text}
        </div>
      )}
      {/* ── Page Header ── */}
      <div className="rh-page-header">
        <div className="rh-header-left">
          <h2>
            <Sparkles size={18} style={{ color: '#6366f1' }} />
            ReplyHub
          </h2>
          <p>AI-powered inbox — auto-tagging, smart replies &amp; drafts</p>
        </div>
        <div className="rh-header-actions">
          <button className="rh-btn rh-btn-ghost rh-btn-sm" onClick={handleRefresh} disabled={listLoading}>
            <RefreshCw size={14} className={listLoading ? 'rh-ai-loading-spin' : ''} />
            Refresh
          </button>
          <button
            className="rh-btn rh-btn-ghost rh-btn-sm"
            onClick={() => setShowAiPanel(v => !v)}
            title={showAiPanel ? 'Hide AI Panel' : 'Show AI Panel'}
          >
            {showAiPanel ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
            AI Panel
          </button>
        </div>
      </div>

      <CampaignDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedCampaignIds={filterParams.campaignIds}
        onChange={(ids) => updateFilter({ campaignIds: ids })}
        qs={qs}
        onNamesLoaded={onDrawerNamesUpdate}
      />

      {/* ── 3-Panel Container ── */}
      <div className="replyhub-container">

        {/* ════════════ LEFT — Message List ════════════ */}
        <div className="rh-message-list">
          <div className="rh-list-header">
            {/* Drawer toggle */}
            <div className="rh-drawer-toggle-row">
              <button
                type="button"
                className="rh-btn rh-btn-ghost rh-btn-sm"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open campaigns drawer"
              >
                <PanelLeftOpen size={14} />
                Campaigns
                {filterParams.campaignIds.length > 0 && (
                  <span className="rh-toggle-badge">{filterParams.campaignIds.length}</span>
                )}
              </button>
            </div>
            {/* Search */}
            <div className="rh-search-box">
              <Search size={14} className="rh-search-icon" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={filterParams.search}
                onChange={e => updateFilter({ search: e.target.value })}
              />
            </div>
            {/* Sort Toggle */}
            <div className="rh-sort-row">
              <span className="rh-sort-label">Sort:</span>
              <div className="rh-sort-toggle">
                <button
                  className={`rh-sort-btn ${filterParams.sort === 'time' ? 'active' : ''}`}
                  onClick={() => updateFilter({ sort: 'time' })}
                >
                  <Clock size={11} />
                  Latest
                </button>
                <button
                  className={`rh-sort-btn ${filterParams.sort === 'priority' ? 'active' : ''}`}
                  onClick={() => updateFilter({ sort: 'priority' })}
                >
                  <TrendingUp size={11} />
                  Priority
                </button>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="rh-filter-tabs">
              {FILTER_TABS.map(tab => (
                <button
                  key={tab.key}
                  className={`rh-filter-tab ${tabIsActive(tab.key) ? 'active' : ''}`}
                  onClick={() => onTabClick(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Campaign selection chips (drawer-driven) */}
            {filterParams.campaignIds.length > 0 && (
              <div className="rh-active-chips">
                {filterParams.campaignIds.map((id) => (
                  <span key={id} className="rh-active-chip">
                    Campaign: {campaignNames[id] || id}
                    <button
                      type="button"
                      className="rh-active-chip__x"
                      aria-label={`Remove campaign ${campaignNames[id] || id}`}
                      onClick={() =>
                        updateFilter({
                          campaignIds: filterParams.campaignIds.filter((x) => x !== id),
                        })
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* More filters panel */}
            <div onMouseDown={ensureAccounts} onFocus={ensureAccounts}>
              <MoreFilters
                value={filterParams}
                onChange={updateFilter}
                accounts={accounts}
                loadingAccounts={loadingAccounts}
              />
            </div>
          </div>

          {/* Conversation List */}
          <div className="rh-conv-list">
            {listLoading && (
              <div className="rh-ai-loading" style={{ padding: '2rem 1rem', flexDirection: 'column' }}>
                <div className="rh-ai-loading-spin" />
                <span>Loading conversations...</span>
              </div>
            )}
            {!listLoading && filtered.length === 0 && (
              <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>
                No conversations found
              </div>
            )}
            {filtered.map(conv => (
              <div
                key={conv.id}
                className={`rh-conv-item ${selected?.id === conv.id ? 'selected' : ''} ${conv.unread ? 'unread' : ''}`}
                onClick={() => handleSelect(conv)}
              >
                <div className={`rh-conv-dot ${conv.unread ? '' : 'read'}`} />
                <div className="rh-conv-body">
                  <div className="rh-conv-top">
                    <span className="rh-conv-sender">{conv.from}</span>
                    <span className="rh-conv-time">{conv.time}</span>
                  </div>
                  <div className="rh-conv-subject">{conv.subject}</div>
                  <div className="rh-conv-preview">{conv.preview}</div>
                  <div className="rh-conv-footer">
                    <AiTagBadge tag={conv.tag} />
                    <span className="rh-campaign-chip">{conv.campaign}</span>
                    {conv.starred && <Star size={11} fill="#f59e0b" color="#f59e0b" />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ════════════ CENTER — Thread View ════════════ */}
        <div className="rh-thread-view">
          {!selected ? (
            <div className="rh-empty-state">
              <div className="rh-empty-icon">
                <MessageSquare size={24} />
              </div>
              <p>Select a conversation to view</p>
            </div>
          ) : (
            <>
              {/* Thread Header */}
              <div className="rh-thread-header">
                <div className="rh-thread-title-row">
                  <h3 className="rh-thread-subject">{selected.subject}</h3>
                  <div className="rh-thread-actions">
                    <button className="rh-icon-btn" title="Star">
                      <Star size={14} fill={selected.starred ? '#f59e0b' : 'none'} color={selected.starred ? '#f59e0b' : 'currentColor'} />
                    </button>
                    <button className="rh-icon-btn" title="Archive">
                      <Archive size={14} />
                    </button>
                    <button className="rh-icon-btn danger" title="Delete">
                      <Trash2 size={14} />
                    </button>
                    <button className="rh-icon-btn" title="More">
                      <MoreHorizontal size={14} />
                    </button>
                  </div>
                </div>
                <div className="rh-thread-meta">
                  <div className="rh-sender-info">
                    <div className="rh-avatar">{selected.from.charAt(0)}</div>
                    <div>
                      <div className="rh-sender-name">{selected.from}</div>
                      <div className="rh-sender-email">{selected.email}</div>
                    </div>
                  </div>
                  <StatusBadge tag={selected.tag} />
                  <div className="rh-timestamp">
                    <Clock size={12} />
                    {selected.time}
                  </div>
                </div>
              </div>

              {/* AI Insight Bar */}
              {insightOpen && selected.aiSummary && (
                <div className="rh-ai-insight">
                  <div className="rh-ai-insight-icon">
                    <Sparkles size={14} />
                  </div>
                  <div className="rh-ai-insight-content">
                    <div className="rh-ai-insight-label">AI Summary</div>
                    <div className="rh-ai-insight-text">{selected.aiSummary}</div>
                  </div>
                  <button className="rh-insight-toggle" onClick={() => setInsightOpen(false)}>
                    <ChevronUp size={14} />
                  </button>
                </div>
              )}
              {!insightOpen && (
                <div style={{ padding: '0 1.25rem 0.5rem', flexShrink: 0 }}>
                  <button
                    className="rh-btn rh-btn-ghost rh-btn-sm"
                    style={{ fontSize: '0.72rem' }}
                    onClick={() => setInsightOpen(true)}
                  >
                    <Sparkles size={11} /> Show AI Summary <ChevronDown size={11} />
                  </button>
                </div>
              )}

              {/* Messages Area */}
              <div className="rh-messages-area">
                {threadLoading && (
                  <div className="rh-ai-loading" style={{ padding: '2rem 0' }}>
                    <div className="rh-ai-loading-spin" />
                    Loading thread...
                  </div>
                )}
                {!threadLoading && (selected.messages ?? []).map(msg => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    convFromName={selected.from}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Section */}
              <div className="rh-reply-section">
                <div className="rh-reply-toolbar">
                  <button
                    className="rh-btn rh-btn-primary rh-btn-sm"
                    onClick={() => { setReplyMode(v => !v); setReplyText('') }}
                  >
                    <Reply size={13} /> Reply
                  </button>
                  <button
                    className="rh-btn rh-btn-ai rh-btn-sm"
                    onClick={handleAiDraft}
                    disabled={aiDrafting}
                  >
                    {aiDrafting ? <span className="rh-btn-spin-dark" /> : <Cpu size={13} />}
                    AI Draft
                  </button>
                  <button className="rh-btn rh-btn-ghost rh-btn-sm">
                    <Forward size={13} /> Forward
                  </button>
                </div>

                {replyMode && (
                  <div className="rh-composer">
                    {aiDrafting && (
                      <div className="rh-ai-drafting">
                        <span className="rh-ai-spin" />
                        AI is crafting your reply...
                      </div>
                    )}
                    <div className="rh-composer-header">
                      <span className="rh-composer-label">Reply to {selected.from}</span>
                    </div>
                    <textarea
                      placeholder="Write your reply..."
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      disabled={aiDrafting}
                    />
                    <div className="rh-composer-footer">
                      <div className="rh-composer-footer-left">
                        <button
                          className="rh-btn rh-btn-primary rh-btn-sm"
                          onClick={handleSendReply}
                          disabled={!replyText.trim() || sendingReply}
                        >
                          {sendingReply ? <span className="rh-btn-spin" /> : <Send size={13} />}
                          Send
                        </button>
                        <button
                          className="rh-btn rh-btn-ai rh-btn-sm"
                          onClick={handleAiImprove}
                          disabled={aiDrafting}
                        >
                          {aiDrafting ? <span className="rh-btn-spin-dark" /> : <Sparkles size={13} />}
                          AI Improve
                        </button>
                      </div>
                      <button
                        className="rh-btn rh-btn-ghost rh-btn-sm"
                        onClick={() => { setReplyMode(false); setReplyText('') }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ════════════ RIGHT — AI Panel ════════════ */}
        <div className={`rh-ai-panel ${showAiPanel ? '' : 'hidden'}`}>
          <div className="rh-ai-panel-header">
            <div className="rh-ai-panel-title">
              <Sparkles size={14} style={{ color: '#6366f1' }} />
              AI Insights
              <span className="rh-ai-badge">AI</span>
            </div>
          </div>

          {!selected ? (
            <div className="rh-ai-loading" style={{ padding: '2rem 1rem', flexDirection: 'column', gap: '0.5rem' }}>
              <MessageSquare size={20} style={{ color: '#e2e8f0' }} />
              <span style={{ textAlign: 'center', fontSize: '0.78rem' }}>Select a conversation</span>
            </div>
          ) : aiLoading && !currentAi ? (
            <div className="rh-ai-loading">
              <div className="rh-ai-loading-spin" />
              Analyzing...
            </div>
          ) : (
            <>
              {/* Classification Card */}
              <div className="rh-ai-card">
                <div className="rh-ai-card-title">
                  <Tag size={12} className="rh-ai-card-icon" />
                  Classification
                </div>
                <div className="rh-classification-result">
                  <AiTagBadge tag={currentClassification?.tag ?? selected.tag} size="large" />
                  <div>
                    <div className="rh-confidence-row">
                      <span>Confidence</span>
                      <span className="rh-confidence-pct">
                        {currentClassification?.confidence ?? selected.confidence}%
                      </span>
                    </div>
                    <div className="rh-confidence-bar">
                      <div
                        className="rh-confidence-fill"
                        style={{ width: `${currentClassification?.confidence ?? selected.confidence}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Sentiment Card */}
              <div className="rh-ai-card">
                <div className="rh-ai-card-title">
                  <TrendingUp size={12} className="rh-ai-card-icon" />
                  Sentiment
                </div>
                <SentimentRow sentiment={currentClassification?.sentiment ?? selected.sentiment} />
              </div>

              {/* Smart Replies Card */}
              <div className="rh-ai-card">
                <div className="rh-ai-card-title">
                  <Zap size={12} className="rh-ai-card-icon" />
                  Smart Replies
                </div>
                <div className="rh-smart-replies">
                  {currentReplies.map((reply, i) => (
                    <button
                      key={i}
                      className="rh-smart-reply-btn"
                      onClick={() => handleSmartReply(reply)}
                      title="Click to use this reply"
                    >
                      <MessageSquare size={12} className="rh-smart-reply-icon" />
                      {reply.length > 80 ? reply.slice(0, 80) + '…' : reply}
                    </button>
                  ))}
                </div>
              </div>

              {/* Next Action Card */}
              <div className="rh-ai-card">
                <div className="rh-ai-card-title">
                  <Target size={12} className="rh-ai-card-icon" />
                  Next Action
                </div>
                <div className="rh-next-action">
                  <div className="rh-action-item">
                    <Lightbulb size={13} className="rh-action-icon" />
                    <div className="rh-action-text">
                      {selected.nextAction}
                    </div>
                  </div>
                  {selected.tag === 'meeting-booked' && (
                    <div className="rh-action-item" style={{ background: 'rgba(99,102,241,0.08)', borderColor: 'rgba(99,102,241,0.2)' }}>
                      <CheckCircle2 size={13} className="rh-action-icon" />
                      <div className="rh-action-text">
                        <strong>High priority</strong> — Prepare a tailored demo before the meeting.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Inbox
